/**
 * proposalsStore — Zustand store keyed by ``doc_id`` holding the queue of
 * pending AI-side document edit proposals.
 *
 * Pipeline:
 *   1. ``WSClient`` parses a ``doc_edit_proposal`` frame (zod schema in
 *      ``@ando/core/ws/schemas``).
 *   2. ``useDocEditProposalBridge`` pushes the proposal into this store.
 *   3. The currently mounted ``<ArtifactPane>`` (with its ``DocxEditorInline``
 *      / ``XlsxEditorInline`` ref) subscribes to its own ``doc_id`` slice,
 *      reads the queue, and invokes ``editor.applyAIProposal(proposal)`` —
 *      which renders a tracked-change suggestion (docx) or a highlighted
 *      cell (xlsx). The user accepts or rejects each item.
 *
 * Why a store and not a transient React state in the pane:
 *   - Proposals can arrive while the pane is unmounted (user navigated to
 *     a different tab). The store holds them until the pane re-mounts.
 *   - Multiple editors can be live concurrently (split-screen, multi-tab).
 *     A doc-keyed map fans out cleanly without prop drilling.
 *
 * Persistence: in-memory only. Proposals are ephemeral (BE re-emits on
 * reconnect via the chat history replay).
 */

import { create } from "zustand";
import type { DocEditProposalEvent } from "@ando/core";

export interface PendingProposal {
  proposal_id: string;
  doc_id: string;
  kind: DocEditProposalEvent["kind"];
  payload: unknown;
  rationale?: string;
  /** Epoch ms — for sort + cleanup older-than-N. */
  received_at: number;
  /**
   * ``true`` once the editor has materialised the proposal as a tracked
   * change / cell highlight (i.e. the visual preview is on screen). Used
   * purely as a dedup token so the ``useEffect`` in ArtifactPane doesn't
   * re-call ``applyAIProposal`` on every re-mount.
   *
   * Distinct from ``decision`` — a proposal can be APPLIED (preview
   * visible) but UNDECIDED (user hasn't clicked Confirm/Reject in either
   * the in-doc chip OR the ActionDrawer). May 23, 2026 SOTA — preview-
   * first HITL pattern (Cursor / ChatGPT Canvas).
   */
  applied: boolean;
  /**
   * ``null`` while pending. ``accepted`` when the user confirms (either
   * via the ActionDrawer Confirm button or by accepting the in-doc
   * tracked-change chip). ``rejected`` on the inverse path.
   *
   * The bidirectional sync between chip and ActionDrawer is owned by
   * ``ArtifactPane`` (the chip side) and ``ActionFormSurface`` (the
   * drawer side) — they BOTH call ``markDecision`` so the other surface
   * collapses automatically.
   */
  decision: "accepted" | "rejected" | null;
}

interface ProposalsState {
  byDoc: Readonly<Record<string, ReadonlyArray<PendingProposal>>>;
  enqueue: (event: DocEditProposalEvent) => void;
  /** Mark the proposal as visually applied in the editor (preview). */
  markApplied: (proposalId: string) => void;
  /** Mark the user's accept/reject decision. */
  markDecision: (
    proposalId: string,
    decision: "accepted" | "rejected",
  ) => void;
  clearForDoc: (docId: string) => void;
}

export const useProposalsStore = create<ProposalsState>((set) => ({
  byDoc: {},
  enqueue: (event) => {
    set((prev) => {
      const list = prev.byDoc[event.doc_id] ?? [];
      // Dedup by proposal_id — BE may re-emit on WS reconnect replay.
      if (list.some((p) => p.proposal_id === event.proposal_id)) return prev;
      const next: PendingProposal = {
        proposal_id: event.proposal_id,
        doc_id: event.doc_id,
        kind: event.kind,
        payload: event.payload,
        rationale: event.rationale,
        received_at: Date.now(),
        applied: false,
        decision: null,
      };
      return {
        byDoc: {
          ...prev.byDoc,
          [event.doc_id]: [...list, next],
        },
      };
    });
  },
  markApplied: (proposalId) => {
    set((prev) => {
      let touched = false;
      const next: Record<string, ReadonlyArray<PendingProposal>> = {};
      for (const [docId, list] of Object.entries(prev.byDoc)) {
        const idx = list.findIndex((p) => p.proposal_id === proposalId);
        if (idx === -1) {
          next[docId] = list;
          continue;
        }
        const prevItem = list[idx];
        if (!prevItem || prevItem.applied) {
          next[docId] = list;
          continue;
        }
        touched = true;
        const mut: PendingProposal[] = [...list];
        mut[idx] = { ...prevItem, applied: true };
        next[docId] = mut;
      }
      return touched ? { byDoc: next } : prev;
    });
  },
  markDecision: (proposalId, decision) => {
    set((prev) => {
      let touched = false;
      const next: Record<string, ReadonlyArray<PendingProposal>> = {};
      for (const [docId, list] of Object.entries(prev.byDoc)) {
        const idx = list.findIndex((p) => p.proposal_id === proposalId);
        if (idx === -1) {
          next[docId] = list;
          continue;
        }
        const prevItem = list[idx];
        if (!prevItem) {
          next[docId] = list;
          continue;
        }
        touched = true;
        const mut: PendingProposal[] = [...list];
        mut[idx] = { ...prevItem, decision };
        next[docId] = mut;
      }
      return touched ? { byDoc: next } : prev;
    });
  },
  clearForDoc: (docId) => {
    set((prev) => {
      if (!prev.byDoc[docId]) return prev;
      const { [docId]: _, ...rest } = prev.byDoc;
      void _;
      return { byDoc: rest };
    });
  },
}));

/**
 * Selector — referentially-stable slice of the by-doc queue for a single
 * ``docId``. Returns the SAME array reference until the store actually
 * mutates that bucket, so React 19 / Zustand 5's
 * ``useSyncExternalStore`` snapshot comparison (``Object.is``) doesn't
 * trip an infinite re-render loop (fixed May 21, 2026 after the
 * test-engineer agent caught a `Maximum update depth exceeded` on every
 * ArtifactPane mount).
 *
 * Callers that need only PENDING (``decision === null``) items must wrap
 * the result in ``useMemo`` themselves — filtering inside the selector
 * would re-create the array reference on every store snapshot.
 */
export function selectByDocSlice(docId: string) {
  return (s: ProposalsState): ReadonlyArray<PendingProposal> | undefined =>
    s.byDoc[docId];
}

/**
 * Pure derivation — proposals that the editor has NOT YET materialised
 * as a tracked-change chip. The ArtifactPane ``useEffect`` consumes this
 * slice, calls ``applyAIProposal()``, then marks them ``applied: true``
 * so they drop out of the queue (without affecting ``decision``, which
 * is the user-facing accept/reject state).
 *
 * Stable when wrapped in ``useMemo([slice])`` because Object.is equality
 * on the slice short-circuits the call.
 */
export function pendingOnly(
  slice: ReadonlyArray<PendingProposal> | undefined,
): ReadonlyArray<PendingProposal> {
  if (!slice || slice.length === 0) return EMPTY_PROPOSAL_ARRAY;
  const filtered = slice.filter((p) => !p.applied && p.decision === null);
  // Returning the same empty constant when nothing pending avoids
  // ``[] !== []`` cache busts in downstream useMemo deps.
  return filtered.length === 0 ? EMPTY_PROPOSAL_ARRAY : filtered;
}

/**
 * Lookup helper — find a proposal by its action proposal_id across the
 * by-doc map. Used by the ActionFormSurface ↔ chip bidirectional sync:
 * when the user clicks Confirm/Reject in the drawer, we need to mark
 * the chip on the editor; when the user accepts/rejects the chip, we
 * need to close the drawer. May 23, 2026.
 */
export function findProposalById(
  state: ProposalsState,
  proposalId: string,
): PendingProposal | null {
  for (const list of Object.values(state.byDoc)) {
    const hit = list.find((p) => p.proposal_id === proposalId);
    if (hit) return hit;
  }
  return null;
}

const EMPTY_PROPOSAL_ARRAY: ReadonlyArray<PendingProposal> = Object.freeze([]);
